import { useMemo } from 'react';
import { formatDayMonth } from '../../../utils/overviewDates';
import { presetRange, validateRange } from '../../../utils/panorama/panoramaPeriod';

interface PeriodPickerProps {
	from: string | null;
	to: string | null;
	onChange: (from: string | null, to: string | null) => void;
	today: Date;
	allowEmpty?: boolean;
	minDate?: string;
}

const PRESET_CLASS = 'w-full rounded-lg border border-raro-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function PeriodPicker({ from, to, onChange, today, allowEmpty = false, minDate }: PeriodPickerProps) {
	const presets = [
		{ key: 'next-30', label: 'Próximos 30 dias' },
		{ key: 'next-90', label: 'Próximos 90 dias' },
		{ key: 'next-180', label: 'Próximos 180 dias' },
		{ key: 'this-month', label: 'Este mês' },
		{ key: 'next-month', label: 'Próximo mês' },
		{ key: 'last-month', label: 'Mês passado' },
	];

	const currentPreset = useMemo(() => {
		if ((!from || !to) && allowEmpty) return 'empty';
		for (const p of presets) {
			const range = presetRange(p.key as any, today);
			if (from === range.from && to === range.to) return p.key;
		}
		return 'custom';
	}, [from, to, allowEmpty, today]);

	const isValid = validateRange(from && to ? { from, to } : null, minDate);
	const hasInvalidMinDate = from && minDate && from < minDate;

	const handlePresetChange = (presetKey: string) => {
		if (presetKey === 'empty') {
			onChange(null, null);
		} else if (presetKey !== 'custom') {
			const range = presetRange(presetKey as any, today);
			onChange(range.from, range.to);
		}
	};

	return (
		<>
			<div>
				<label className="mb-1 block text-xs font-medium text-raro-gray-600">Período</label>
				<select
					value={currentPreset}
					onChange={e => handlePresetChange(e.target.value)}
					className={PRESET_CLASS}
				>
					{allowEmpty && <option value="empty">Sem período</option>}
					{presets.map(p => (
						<option key={p.key} value={p.key}>
							{p.label}
						</option>
					))}
					<option value="custom">Personalizado</option>
				</select>
			</div>

			<div>
				<label className="mb-1 block text-xs font-medium text-raro-gray-600">De</label>
				<input
					type="date"
					value={from || ''}
					onChange={e => onChange(e.target.value || null, to)}
					min={minDate}
					max={to ?? undefined}
					className={PRESET_CLASS}
				/>
			</div>

			<div>
				<label className="mb-1 block text-xs font-medium text-raro-gray-600">Até</label>
				<input
					type="date"
					value={to || ''}
					onChange={e => onChange(from, e.target.value || null)}
					min={from || minDate}
					className={PRESET_CLASS}
				/>
			</div>

			{hasInvalidMinDate && (
				<div className="col-span-full text-xs text-red-600">
					Não há férias registradas antes de {minDate?.split('-').reverse().join('/')}
				</div>
			)}

			{from && to && !isValid && (
				<div className="col-span-full text-xs text-red-600">
					Período inválido (máximo 366 dias, fim não pode ser antes do início)
				</div>
			)}
		</>
	);
}
